import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  AlertTriangle,
  BadgeCheck,
  CircleDollarSign,
  Hash,
  LoaderCircle,
  ReceiptText,
  ShieldAlert,
} from "lucide-react";
import {
  InvoiceVerificationResult,
  verifyInvoiceAuthenticity,
} from "../services/invoice-verification";

const formatAmount = (
  amount: number | string | undefined,
  currency: string | undefined,
  locale: string,
  fallback: string,
) => {
  if (amount === undefined || amount === null || amount === "") {
    return fallback;
  }

  const numericAmount =
    typeof amount === "number" ? amount : Number.parseFloat(String(amount));

  if (!Number.isNaN(numericAmount)) {
    try {
      return new Intl.NumberFormat(locale, {
        currency: currency || "QAR",
        style: "currency",
      }).format(numericAmount);
    } catch (error) {
      return `${currency || "QAR"} ${numericAmount.toFixed(2)}`;
    }
  }

  return `${currency ? `${currency} ` : ""}${amount}`;
};

const DetailRow = ({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof Hash;
  label: string;
  value: string;
}) => (
  <div className="rounded-2xl border border-white/60 bg-white/80 p-4 shadow-sm backdrop-blur">
    <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.24em] text-slate-500">
      <Icon className="h-4 w-4" />
      <span>{label}</span>
    </div>
    <p className="text-base font-semibold text-slate-900 break-words">{value}</p>
  </div>
);

const InvoiceVerificationPage = () => {
  const { t, i18n } = useTranslation();
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState<InvoiceVerificationResult | null>(null);

  useEffect(() => {
    let active = true;

    const runVerification = async () => {
      if (!invoiceId) {
        if (active) {
          setResult({
            endpoint: "",
            invoiceId: "",
            messageKey: "invoice-verification.message.missing-id",
            state: "unavailable",
          });
          setLoading(false);
        }
        return;
      }

      setLoading(true);
      const response = await verifyInvoiceAuthenticity(invoiceId);

      if (active) {
        setResult(response);
        setLoading(false);
      }
    };

    runVerification();

    return () => {
      active = false;
    };
  }, [invoiceId]);

  const verificationState = result?.state || "unavailable";
  const isGenuine = verificationState === "genuine";
  const isSuspicious = verificationState === "not_genuine";
  const notAvailable = t("invoice-verification.not-available");
  const locale = i18n.resolvedLanguage || i18n.language || "en";
  const message =
    result?.messageKey && !result?.message
      ? t(result.messageKey)
      : result?.message || (result?.messageKey ? t(result.messageKey) : "");

  return (
    <div className="min-h-screen overflow-hidden bg-[#f6efe8] text-slate-900">
      <style>{`
        @keyframes invoiceGlow {
          0%, 100% { transform: scale(1); opacity: 0.9; }
          50% { transform: scale(1.05); opacity: 1; }
        }

        @keyframes riseIn {
          from { opacity: 0; transform: translateY(24px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .invoice-glow {
          animation: invoiceGlow 2.8s ease-in-out infinite;
        }

        .rise-in {
          animation: riseIn 0.55s ease-out both;
        }
      `}</style>

      <div className="relative isolate">
        <div className="absolute inset-x-0 top-0 h-[420px] bg-[radial-gradient(circle_at_top,_rgba(15,23,42,0.12),_transparent_55%),linear-gradient(135deg,_#fff7ed,_#e2e8f0)]" />
        <div className="absolute -left-12 top-20 h-40 w-40 rounded-full bg-[#d97706]/15 blur-3xl" />
        <div className="absolute right-0 top-12 h-56 w-56 rounded-full bg-[#0f172a]/10 blur-3xl" />

        <div className="relative mx-auto flex min-h-screen max-w-6xl items-center px-4 py-12 sm:px-6 lg:px-8">
          <div className="w-full">
            <section className="rise-in mx-auto max-w-3xl rounded-[32px] border border-white/70 bg-white/75 p-6 shadow-[0_24px_70px_rgba(15,23,42,0.12)] backdrop-blur-xl sm:p-8">
              <div
                className={`mb-8 rounded-[28px] border p-5 sm:p-6 ${
                  loading
                    ? "border-slate-200 bg-slate-50"
                    : isGenuine
                      ? "border-emerald-200 bg-gradient-to-r from-emerald-50 to-emerald-100/70"
                      : isSuspicious
                        ? "border-rose-200 bg-gradient-to-r from-rose-50 to-orange-50"
                        : "border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50"
                }`}
              >
                <div className="mb-6 flex items-start justify-between gap-4">
                  <div>
                    <p className="mb-3 inline-flex rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.28em] text-slate-600">
                      {t("invoice-verification.badge")}
                    </p>
                    <h1 className="text-3xl font-black tracking-tight text-slate-950 sm:text-5xl">
                      {loading
                        ? t("invoice-verification.heading.loading")
                        : isGenuine
                          ? t("invoice-verification.heading.genuine")
                          : isSuspicious
                            ? t("invoice-verification.heading.not-genuine")
                            : t("invoice-verification.heading.unavailable")}
                    </h1>
                    <p className="mt-4 max-w-2xl text-sm leading-7 text-slate-600 sm:text-base">
                      {loading
                        ? t("invoice-verification.description.loading")
                        : isGenuine
                          ? t("invoice-verification.description.genuine")
                          : isSuspicious
                            ? t("invoice-verification.description.not-genuine")
                            : t("invoice-verification.description.unavailable")}
                    </p>
                  </div>

                  <div
                    className={`invoice-glow flex h-20 w-20 shrink-0 items-center justify-center rounded-[28px] border ${
                      loading
                        ? "border-slate-200 bg-slate-100 text-slate-700"
                        : isGenuine
                          ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                          : isSuspicious
                            ? "border-rose-200 bg-rose-50 text-rose-700"
                            : "border-amber-200 bg-amber-50 text-amber-700"
                    }`}
                  >
                    {loading ? (
                      <LoaderCircle className="h-10 w-10 animate-spin" />
                    ) : isGenuine ? (
                      <BadgeCheck className="h-10 w-10" />
                    ) : isSuspicious ? (
                      <ShieldAlert className="h-10 w-10" />
                    ) : (
                      <AlertTriangle className="h-10 w-10" />
                    )}
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="rounded-2xl bg-white/80 p-3">
                    <ReceiptText className="h-7 w-7 text-slate-900" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-500">
                      {t("invoice-verification.invoice-id")}
                    </p>
                    <p className="mt-2 break-all text-lg font-bold text-slate-950 sm:text-2xl">
                      {invoiceId || notAvailable}
                    </p>
                    {message && (
                      <p className="mt-3 text-sm leading-6 text-slate-700">
                        {message}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-3">
                <DetailRow
                  icon={Hash}
                  label={t("invoice-verification.invoice-number")}
                  value={result?.invoiceNumber || notAvailable}
                />
                <DetailRow
                  icon={Hash}
                  label={t("invoice-verification.order-number")}
                  value={result?.orderNumber || notAvailable}
                />
                <DetailRow
                  icon={CircleDollarSign}
                  label={t("invoice-verification.amount")}
                  value={formatAmount(
                    result?.amount,
                    result?.currency,
                    locale,
                    notAvailable,
                  )}
                />
              </div>
              <div className="mt-8 flex flex-wrap gap-3 pt-2">
                <button
                  className="rounded-full bg-[#0f172a] px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                  disabled={loading}
                  onClick={() => window.location.reload()}
                  type="button"
                >
                  {t("invoice-verification.try-again")}
                </button>
                <Link
                  className="rounded-full border border-slate-300 px-5 py-3 text-sm font-semibold text-slate-900 transition hover:bg-white/80"
                  to="/"
                >
                  {t("invoice-verification.back-home")}
                </Link>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceVerificationPage;
